import type { Table } from '@tanstack/react-table'
import { DataTableFilterAdapter } from 'blocks/data-table/data-table-faceted-filter'
import type { DataTableFilterField } from 'blocks/data-table/types'
import { cn } from 'lib/utils'
import { Input } from 'primitives/base/input'
import { MonthYearRangePicker } from 'primitives/month-year-range-picker'
import * as React from 'react'

// import { DataTableViewOptions } from 'blocks/data-table/data-table-view-options'

interface DataTableToolbarProps<TData> extends React.HTMLAttributes<HTMLDivElement> {
  table: Table<TData>
  filterFields?: DataTableFilterField<TData>[]
}

export const DataTableToolbar = <TData,>({
  table,
  filterFields = [],
  children,
  className,
  ...props
}: DataTableToolbarProps<TData>) => {
  // Memoize computation of searchableColumns, filterableColumns and booleanColumns
  const { searchableColumns, facetedFields, monthYearRangeFields } = React.useMemo(
    () => ({
      searchableColumns: filterFields.filter(
        (field) =>
          !field.options &&
          !field.isBoolean &&
          !field.isDateRange &&
          !field.isDurationRange &&
          !field.isMonthYearRange &&
          !field.showInMoreFilters,
      ),
      facetedFields: filterFields.filter(
        (field) =>
          (field.options ||
            field.isBoolean ||
            field.isDateRange ||
            field.isDurationRange ||
            field.showInMoreFilters ||
            field.remoteOptionsConfig) &&
          !field.isMonthYearRange,
      ),
      monthYearRangeFields: filterFields.filter((field) => field.isMonthYearRange),
    }),
    [filterFields],
  )

  const dataForFilters = table.getPreFilteredRowModel().rows.map((row) => row.original)

  return (
    <div
      className={cn('flex w-full flex-wrap items-start justify-between gap-2 p-0 pb-3', className)}
      {...props}
    >
      <div className="flex min-w-0 flex-1 basis-80 flex-wrap items-center gap-2">
        {searchableColumns.length > 0 &&
          searchableColumns.map((column) => {
            const tableColumn = table.getColumn(column.value ? String(column.value) : '')
            if (!tableColumn) return null
            return (
              <Input
                key={String(column.value)}
                placeholder={column.placeholder}
                value={(tableColumn.getFilterValue() as string) ?? ''}
                onChange={(event) => tableColumn.setFilterValue(event.target.value)}
                // 30px, not the Input default 36px: the toolbar row runs one
                // step denser than form fields. Set here rather than on Input
                // so every form in the console keeps its own height.
                className={cn(
                  'h-[30px] w-40 max-w-full font-inter text-s-400 normal-case tracking-normal lg:w-64',
                  column.className,
                )}
              />
            )
          })}
        {monthYearRangeFields.map((field) => {
          const col = table.getColumn(String(field.value))
          return (
            <MonthYearRangePicker
              key={String(field.value)}
              value={(col?.getFilterValue() as string) ?? ''}
              onChange={(val) => col?.setFilterValue(val)}
              disableFutureDates={field.dateRangeOptions?.disableFuture}
              placeholder={field.label}
              className="h-[30px] max-w-full py-0"
            />
          )
        })}
        {facetedFields.length > 0 && (
          <DataTableFilterAdapter
            table={table}
            filterFields={facetedFields}
            data={dataForFilters}
            strategy={table.options.manualFiltering ? 'server' : 'client'}
          />
        )}
      </div>
      {/*
        Action buttons come from ~37 call sites, effectively all at the Button
        default (36px). Stepping them down centrally keeps them level with the
        30px filters and search without touching every page. Height and type
        only — width is left alone so nothing with an intrinsic width squashes.
      */}
      <div className="ml-auto flex max-w-full flex-wrap gap-2 self-start [&_button]:h-[30px] [&_button]:px-3">
        {children}
        {/* Keep this commented out for now, uncomment when needed */}
        {/* <DataTableViewOptions table={table} /> */}
      </div>
    </div>
  )
}
